
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Settings, 
  Save, 
  RefreshCw,
  Award,
  BookOpen,
  Sliders,
  AlertTriangle
} from 'lucide-react';
import { UniversalCourseRegistry } from '@/services/universalCourseRegistry';
import { toast } from '@/components/ui/sonner';

interface CourseSettings {
  courseId: string;
  certificatesEnabled: boolean;
  minimumScore: number;
  requireAllModules: boolean;
  autoGenerate: boolean;
}

const CertificateToggleControls: React.FC = () => {
  const [courseSettings, setCourseSettings] = useState<Record<string, CourseSettings>>({});
  const [globalSettings, setGlobalSettings] = useState({
    certificatesEnabled: true,
    defaultMinimumScore: 80,
    autoGenerationEnabled: true,
    securityValidationEnabled: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCourseSettings();
  }, []);

  const loadCourseSettings = async () => {
    setLoading(true);
    try {
      // Get all registered courses from UniversalCourseRegistry
      const courses = UniversalCourseRegistry.getAllRegisteredCourses();
      
      // Initialize default settings for each course
      const defaultSettings: Record<string, CourseSettings> = {};
      courses.forEach(course => {
        defaultSettings[course.courseId] = {
          courseId: course.courseId,
          certificatesEnabled: true,
          minimumScore: 80,
          requireAllModules: true,
          autoGenerate: true
        };
      });
      
      setCourseSettings(defaultSettings);
      console.log(`Loaded settings for ${courses.length} courses`);
    } catch (error) {
      console.error('Error loading course settings:', error);
      toast.error('Failed to load course settings');
    } finally {
      setLoading(false);
    }
  };

  const updateCourseSettings = (courseId: string, updates: Partial<CourseSettings>) => {
    setCourseSettings(prev => ({
      ...prev,
      [courseId]: {
        ...prev[courseId],
        ...updates
      }
    }));
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      // In a real implementation, this would save to the database
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      
      toast.success('Certificate settings saved successfully');
      console.log('Saved settings:', { courseSettings, globalSettings });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleBulkCertificates = (enabled: boolean) => {
    const updated = { ...courseSettings };
    Object.keys(updated).forEach(courseId => {
      updated[courseId].certificatesEnabled = enabled;
    });
    setCourseSettings(updated);
    toast.info(`${enabled ? 'Enabled' : 'Disabled'} certificates for all courses`);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p>Loading course settings...</p>
        </CardContent>
      </Card>
    );
  }

  const coursesByType = Object.values(courseSettings).reduce((acc, course) => {
    const courseInfo = UniversalCourseRegistry.getCourseInfo(course.courseId);
    const type = courseInfo?.courseType || 'other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(course);
    return acc;
  }, {} as Record<string, CourseSettings[]>);

  return (
    <div className="space-y-6">
      {/* Global Settings */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Global Certificate Settings
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => toggleBulkCertificates(true)}
              variant="outline"
              size="sm"
            >
              Enable All
            </Button>
            <Button
              onClick={() => toggleBulkCertificates(false)}
              variant="outline"
              size="sm"
            >
              Disable All
            </Button>
            <Button onClick={saveSettings} disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Master Certificate Toggle</label>
              <Switch
                checked={globalSettings.certificatesEnabled}
                onCheckedChange={(checked) => 
                  setGlobalSettings(prev => ({ ...prev, certificatesEnabled: checked }))
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Auto Generation</label>
              <Switch
                checked={globalSettings.autoGenerationEnabled}
                onCheckedChange={(checked) => 
                  setGlobalSettings(prev => ({ ...prev, autoGenerationEnabled: checked }))
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Security Validation</label>
              <Switch
                checked={globalSettings.securityValidationEnabled}
                onCheckedChange={(checked) => 
                  setGlobalSettings(prev => ({ ...prev, securityValidationEnabled: checked }))
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Default Min Score</label>
              <input
                type="number"
                min="0"
                max="100"
                value={globalSettings.defaultMinimumScore}
                onChange={(e) => 
                  setGlobalSettings(prev => ({ ...prev, defaultMinimumScore: parseInt(e.target.value) }))
                }
                className="w-16 px-2 py-1 border rounded text-sm"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Course-Specific Settings */}
      <Tabs defaultValue={Object.keys(coursesByType)[0]} className="space-y-4">
        <TabsList className="flex-wrap">
          {Object.entries(coursesByType).map(([type, courses]) => (
            <TabsTrigger key={type} value={type} className="flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              {type.toUpperCase()} ({courses.length})
            </TabsTrigger>
          ))}
        </TabsList>

        {Object.entries(coursesByType).map(([type, courses]) => (
          <TabsContent key={type} value={type}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="h-5 w-5" />
                  {type.toUpperCase()} Course Certificate Settings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {courses.map(course => {
                    const courseInfo = UniversalCourseRegistry.getCourseInfo(course.courseId);
                    return (
                      <div key={course.courseId} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium">{courseInfo?.courseTitle || course.courseId}</h4>
                            <Badge variant={course.certificatesEnabled ? "default" : "secondary"}>
                              {course.certificatesEnabled ? 'Enabled' : 'Disabled'}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {courseInfo?.totalModules || 0} modules • Min Score: {course.minimumScore}%
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <label className="text-sm">Enabled</label>
                            <Switch
                              checked={course.certificatesEnabled}
                              onCheckedChange={(checked) => 
                                updateCourseSettings(course.courseId, { certificatesEnabled: checked })
                              }
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <label className="text-sm">Auto Gen</label>
                            <Switch
                              checked={course.autoGenerate}
                              onCheckedChange={(checked) => 
                                updateCourseSettings(course.courseId, { autoGenerate: checked })
                              }
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <label className="text-sm">Min Score</label>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              value={course.minimumScore}
                              onChange={(e) => 
                                updateCourseSettings(course.courseId, { minimumScore: parseInt(e.target.value) })
                              }
                              className="w-16 px-2 py-1 border rounded text-sm"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Settings Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sliders className="h-5 w-5" />
            Settings Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">
                {Object.values(courseSettings).filter(c => c.certificatesEnabled).length}
              </p>
              <p className="text-sm text-muted-foreground">Courses Enabled</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-red-600">
                {Object.values(courseSettings).filter(c => !c.certificatesEnabled).length}
              </p>
              <p className="text-sm text-muted-foreground">Courses Disabled</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">
                {Object.values(courseSettings).filter(c => c.autoGenerate).length}
              </p>
              <p className="text-sm text-muted-foreground">Auto Generation</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-orange-600">
                {Math.round(Object.values(courseSettings).reduce((sum, c) => sum + c.minimumScore, 0) / Object.values(courseSettings).length)}%
              </p>
              <p className="text-sm text-muted-foreground">Avg Min Score</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CertificateToggleControls;
